import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, SkipForward, X } from "lucide-react";
import { api } from "../lib/api";
import { MUSIC_ASK_TEXT } from "../constants/site";

const MusicCtx = createContext(null);
export const useMusic = () => useContext(MusicCtx);

export function resolveAudioUrl(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  if (/^https?:/i.test(url)) return url;
  return `${window.location.origin}${url}`;
}

export function MusicProvider({ children }) {
  const { pathname } = useLocation();
  const onAdmin = pathname.startsWith("/admin");
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const audioRef = useRef(null);
  const tracksRef = useRef([]);
  const idxRef = useRef(0);
  tracksRef.current = tracks;

  useEffect(() => {
    api
      .get("/music")
      .then(({ data }) => {
        const list = [data.featured, ...(data.playlist || [])].filter((t) => t && t.url);
        setTracks(list);
        if (list.length && !localStorage.getItem("rt-music-asked")) setPromptOpen(true);
      })
      .catch(() => {});
  }, []);

  const ensureAudio = () => {
    if (audioRef.current) return audioRef.current;
    const audio = new Audio();
    audio.volume = 0.6;
    audio.addEventListener("play", () => setPlaying(true));
    audio.addEventListener("pause", () => setPlaying(false));
    audio.addEventListener("ended", () => {
      const list = tracksRef.current;
      if (list.length > 1) {
        const n = (idxRef.current + 1) % list.length;
        idxRef.current = n;
        setCurrent(n);
        audio.src = resolveAudioUrl(list[n].url);
        audio.dataset.trackUrl = list[n].url;
        audio.play().catch(() => {});
      }
    });
    audioRef.current = audio;
    return audio;
  };

  const play = async (i = 0) => {
    const list = tracksRef.current;
    const t = list[i];
    if (!t) return;
    const audio = ensureAudio();
    if (audio.dataset.trackUrl !== t.url) {
      audio.src = resolveAudioUrl(t.url);
      audio.dataset.trackUrl = t.url;
      idxRef.current = i;
      setCurrent(i);
    }
    try {
      await audio.play();
      setVisible(true);
    } catch (e) {
      /* autoplay bloqueado: aguarda o gesto do visitante */
    }
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio || !audio.src) {
      play(idxRef.current);
      return;
    }
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const playTrack = (i) => {
    const audio = audioRef.current;
    const t = tracksRef.current[i];
    if (!t) return;
    if (audio && audio.dataset.trackUrl === t.url) {
      toggle();
      return;
    }
    play(i);
  };

  const next = () => {
    const list = tracksRef.current;
    if (list.length) play((idxRef.current + 1) % list.length);
  };

  const stop = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    setVisible(false);
  };

  const answerPrompt = (yes) => {
    localStorage.setItem("rt-music-asked", "1");
    setPromptOpen(false);
    if (yes) play(0);
  };

  const track = tracks[current] || null;
  const featured = tracks.length ? tracks[0] : null;

  return (
    <MusicCtx.Provider
      value={{ tracks, featured, playlist: tracks.slice(1), track, current, playing, visible, play, playTrack, toggle, next, stop }}
    >
      {children}

      <AnimatePresence>
        {promptOpen && !onAdmin && (
          <motion.div
            key="music-ask"
            data-testid="music-prompt"
            className="fixed inset-0 z-[90] grid place-items-center bg-ink/35 px-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 18 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="paper-card gold-frame w-full max-w-sm p-8 text-center"
            >
              <img src="/images/floral-top.png" alt="" aria-hidden className="mx-auto -mt-16 mb-2 w-44" />
              <p className="font-serif text-2xl leading-snug text-ink">{MUSIC_ASK_TEXT}</p>
              <div className="mt-7 flex justify-center gap-3">
                <button data-testid="music-prompt-yes" onClick={() => answerPrompt(true)} className="btn-gold">
                  Sim
                </button>
                <button data-testid="music-prompt-no" onClick={() => answerPrompt(false)} className="btn-outline">
                  Não
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {visible && track && !onAdmin && (
          <motion.div
            key="mini-player"
            data-testid="mini-player"
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="paper-card gold-frame fixed bottom-4 left-4 right-4 z-[80] flex items-center gap-3 px-4 py-3 sm:left-auto sm:w-80"
          >
            <button
              onClick={toggle}
              data-testid="mini-play-btn"
              aria-label={playing ? "Pausar música" : "Tocar música"}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink text-paper transition-colors hover:bg-sage-dark"
            >
              {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate font-script text-xl leading-none text-ink">{track.title}</p>
              <p className="mt-1 truncate font-sans text-[10px] uppercase tracking-[0.22em] text-sage-dark">{track.artist}</p>
            </div>
            {tracks.length > 1 && (
              <button onClick={next} data-testid="mini-next-btn" aria-label="Próxima música" className="shrink-0 text-sage-dark transition-colors hover:text-ink">
                <SkipForward size={16} />
              </button>
            )}
            <button onClick={stop} data-testid="mini-close-btn" aria-label="Fechar player" className="shrink-0 text-muted transition-colors hover:text-ink">
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </MusicCtx.Provider>
  );
}
